import { useEffect, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Divider } from '@astryxdesign/core/Divider';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Heading } from '@astryxdesign/core/Heading';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { Token } from '@astryxdesign/core/Token';
import { useToast } from '@astryxdesign/core/Toast';
import { CalendarBlank, MapPin, NavigationArrow, Phone } from '@phosphor-icons/react';
import { useBookingNav, useStudio } from '../app/studio';
import { ReminderControls } from '../components/ReminderControls';
import { api, humanError } from '../lib/api';
import { forgetBooking, saveBooking, savedBookings, type SavedBooking } from '../lib/local';
import { fmt, money } from '../lib/time';
import type { BookingStatus, ClientBooking } from '../lib/types';

const STATUS: Record<BookingStatus, { label: string; color: 'blue' | 'green' | 'gray' | 'red' | 'yellow' }> = {
  booked: { label: 'Записаны', color: 'blue' },
  accepted: { label: 'Машина в работе', color: 'yellow' },
  ready: { label: 'Готово', color: 'green' },
  cancelled: { label: 'Отменена', color: 'gray' },
  no_show: { label: 'Не состоялась', color: 'red' },
};

export function MyBookingPage() {
  const studio = useStudio();
  const booking = useBookingNav();
  const [list, setList] = useState<SavedBooking[]>(() => savedBookings(studio.slug));

  // a booking link shared by the studio: /s/<slug>/my#t=<token>
  useEffect(() => {
    const m = location.hash.match(/t=([0-9a-f]{64})/);
    if (!m) return;
    window.history.replaceState(null, '', location.pathname + location.search); // token never stays in the address bar
    void api.myBooking(m[1]).then((b) => {
      if (b && b.studio.slug === studio.slug) {
        saveBooking(studio.slug, { token: m[1], id: b.id, startsAt: b.starts_at, serviceName: b.service_name });
        setList(savedBookings(studio.slug));
      }
    });
  }, [studio.slug]);

  useEffect(() => {
    const l = () => setList(savedBookings(studio.slug));
    window.addEventListener('studio-bookings', l);
    return () => window.removeEventListener('studio-bookings', l);
  }, [studio.slug]);

  const results = useQueries({
    queries: list.map((s) => ({
      queryKey: ['my-booking', s.token],
      queryFn: () => api.myBooking(s.token),
      staleTime: 20_000,
    })),
  });

  const loaded = results.map((r, i) => ({ saved: list[i], q: r }));
  const now = Date.now();
  const upcoming = loaded.filter((x) => !x.q.data || (['booked', 'accepted'].includes(x.q.data.status) && new Date(x.q.data.work_ends_at).getTime() > now - 6 * 3600e3));
  const past = loaded.filter((x) => !upcoming.includes(x));

  return (
    <main className="app-page app-page--top">
      <VStack gap={6} paddingInline={4}>
        <Heading level={1}>Моя запись</Heading>
        {list.length === 0 && (
          <EmptyState
            icon={<CalendarBlank size={40} aria-hidden />}
            title="Записей пока нет"
            description="Запишитесь онлайн — запись появится здесь. Регистрация не нужна."
            actions={<Button label="Записаться" variant="primary" onClick={() => booking.open()} />}
          />
        )}
        {upcoming.map((x) => (
          <BookingCard key={x.saved.id} saved={x.saved} q={x.q} />
        ))}
        {list.length > 0 && <Button label="Записаться ещё" variant="secondary" onClick={() => booking.open()} />}
        {past.length > 0 && (
          <VStack gap={3}>
            <Heading level={2}>История</Heading>
            {past.map((x) => (
              <BookingCard key={x.saved.id} saved={x.saved} q={x.q} compact />
            ))}
          </VStack>
        )}
      </VStack>
    </main>
  );
}

function BookingCard({
  saved,
  q,
  compact,
}: {
  saved: SavedBooking;
  q: { data?: ClientBooking | null; isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  compact?: boolean;
}) {
  const studio = useStudio();
  const toast = useToast();
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  if (q.isPending) return <Skeleton height={180} />;
  if (q.isError)
    return (
      <Card padding={4}>
        <VStack gap={2}>
          <Text>{saved.serviceName}</Text>
          <Text type="supporting">{humanError(q.error)}</Text>
          <Button label="Обновить" size="sm" onClick={() => void q.refetch()} />
        </VStack>
      </Card>
    );
  const b = q.data;
  if (!b) {
    return (
      <Card padding={4} variant="muted">
        <HStack hAlign="between" vAlign="center">
          <Text type="supporting">Запись «{saved.serviceName}» не найдена</Text>
          <Button label="Убрать" size="sm" variant="ghost" onClick={() => forgetBooking(studio.slug, saved.id)} />
        </HStack>
      </Card>
    );
  }
  const st = STATUS[b.status];
  return (
    <Card padding={compact ? 4 : 5}>
      <VStack gap={compact ? 1 : 3}>
        <HStack gap={2} wrap="wrap">
          <Token label={st.label} color={st.color} size="sm" />
          {b.is_demo && <Token label="демо-запись" size="sm" color="gray" />}
        </HStack>
        <Text weight="semibold" type={compact ? 'body' : 'large'}>
          {fmt(b.starts_at, studio.timezone, compact ? 'd MMM, HH:mm' : 'EEEE, d MMMM · HH:mm')}
        </Text>
        <Text>{b.service_name}</Text>
        <Text type="supporting">
          {b.car} · {money(b.price, b.currency, b.price_from)}
          {b.resource_name ? ` · ${b.resource_name}` : ''}
        </Text>
        {!compact && (
          <>
            <Divider />
            <HStack gap={2} vAlign="start">
              <MapPin size={18} aria-hidden />
              <Text type="supporting">{studio.address}</Text>
            </HStack>
            <HStack gap={2} wrap="wrap">
              {studio.map_url && (
                <Button label="Маршрут" size="sm" icon={<NavigationArrow size={16} aria-hidden />} href={studio.map_url} target="_blank" rel="noopener" />
              )}
              {studio.phone && <Button label="Позвонить" size="sm" icon={<Phone size={16} aria-hidden />} href={`tel:${studio.phone.replace(/[^\d+]/g, '')}`} />}
            </HStack>
            {b.status === 'booked' && <ReminderControls booking={b} token={saved.token} onChanged={() => void q.refetch()} />}
            {b.status === 'booked' &&
              (b.can_cancel ? (
                <Button label="Отменить запись" variant="ghost" onClick={() => setConfirm(true)} />
              ) : (
                <Text type="supporting">
                  Онлайн-отмена была доступна до {fmt(b.cancel_deadline, studio.timezone, 'd MMMM HH:mm')}. Чтобы отменить или перенести, позвоните в студию.
                </Text>
              ))}
          </>
        )}
      </VStack>
      <AlertDialog
        isOpen={confirm}
        onOpenChange={setConfirm}
        title="Отменить запись?"
        description={`${b.service_name}, ${fmt(b.starts_at, studio.timezone, 'd MMMM в HH:mm')}. Время освободится для других клиентов.`}
        actionLabel="Отменить запись"
        cancelLabel="Оставить"
        isActionLoading={cancelling}
        onAction={async () => {
          setCancelling(true);
          try {
            const r = await api.cancelMyBooking(saved.token);
            qc.setQueryData(['my-booking', saved.token], r);
            void qc.invalidateQueries({ queryKey: ['slots', studio.slug] });
            toast({ body: 'Запись отменена' });
            setConfirm(false);
          } catch (e) {
            toast({ body: humanError(e), type: 'error' });
          } finally {
            setCancelling(false);
          }
        }}
      />
    </Card>
  );
}
